/* eslint-disable unicorn/prevent-abbreviations */
import angular from 'angular';
import _ from 'lodash-es';
import APIV5 from '~/models/api';

/* globals Knitiv */

/**
 * @param {Object} obj
 * @returns {[import('@knitiv/api-client-javascript').KNode, any][]}
 */
const kidEntries = obj=> /** @type {any} */ (Object.entries(obj));

/**
 * @typedef {any} KTranslate
 * @param {Knitiv.RootScope} $rootScope
 * @param {import('angular').ITimeoutService} $timeout
 * @returns {*}
 * @property {Function} request
 * @property {Function} remove
 * @property {Function} set
 */
export const KTranslate = /* @ngInject */ ($rootScope, $timeout) => {
	let loading = false;

	/** @type {import('~/../typings/ktranslate').KTranslate.Translations} */
	const translations = {};

	/**
	 * @returns {import('@knitiv/api-client-javascript').KNode[]}
	 */
	const translationKeys = () => /** @type {any} */ (Object.keys(translations));

	const performBatchCall = async () => {
		loading = false;
		const lang = $rootScope.appConfig.lang.code;

		// 1. Lister les KIDs qui attendent une réponse (ceux qui ont des 'items')
		const kidsToFetch = translationKeys().filter(kid => translations[kid].items.length > 0);

		if (kidsToFetch.length === 0) {
			return;
		}

		try {
			// 2. APPEL UNIQUE À L'API
			// On suppose que repreListSimple accepte un tableau de KIDs
			const repreList = await APIV5.instance.repreListSimple(
				{ items: kidsToFetch, mode: 'simple' },
				{ id: 'ktranslate.call' },
			);

			// 3. Distribution des résultats
			for (const [kid, text] of kidEntries(repreList.result)) {
				// Mise à jour du cache pour les futurs appels immédiats
				translations[kid].values[lang] = text;

				// Résolution de toutes les promesses en attente pour ce KID
				for (const item of translations[kid].items) {
					if (item.scope) {
						// Mise à jour du scope (compatibilité avec votre ancienne logique)
						if (typeof item.scope.setText === 'function') {
							item.scope.setText(text);
						}
						else {
							item.scope.text = text;
						}
					}
					item.resolve(text); // Débloque le 'await' dans request()
				}

				// On vide la file d'attente pour ce KID
				translations[kid].items = [];
			}
		}
		catch (error) {
			console.error('KTranslate: Erreur lors de l\'appel batch', error);
			// En cas d'erreur, on résout avec le KID pour ne pas bloquer l'UI
			for (const kid of kidsToFetch) {
				for (const item of translations[kid].items) {
					item.resolve(kid);
				}
				translations[kid].items = [];
			}
		}
	};

	$rootScope.$on('changeLanguage', performBatchCall);

	return {
		/**
		 * @param {import('~/../typings/ktranslate').KTranslate.TranslationItem['scope']} scope
		 * @param {import('@knitiv/api-client-javascript').KNode} kid
		 * @returns {Promise<string>}
		 */
		request: async (scope, kid) => {
			const lang = $rootScope.appConfig.lang.code;

			// 1. Si déjà en cache pour cette langue, retour immédiat
			if (translations[kid]?.values[lang]) {
				const cachedText = translations[kid].values[lang];
				if (scope) {
					scope.text = cachedText;
				}
				return cachedText;
			}

			// 2. Initialisation de l'entrée si inexistante
			if (!translations[kid]) {
				translations[kid] = {
					items:  [],
					values: {},
				};
			}

			// 3. Création d'une promesse locale pour cet appel
			let resolveFn;
			const promise = new Promise(resolve => {
				resolveFn = resolve;
			});

			if (resolveFn) {
				// On ajoute le "ticket" dans la file d'attente du KID
				translations[kid].items.push({
					scope,
					resolve: resolveFn,
				});

				// 4. Déclenchement du compte à rebours de 250ms
				if (!loading) {
					loading = true;
					$timeout(performBatchCall, 250);
				}
			}

			// 5. On attend que performBatchCall appelle resolveFn
			return await promise;
		},

		/**
		 * @param {import('~/../typings/ktranslate').KTranslate.TranslationItem['scope']['$id']} scopeId
		 */
		remove: scopeId => {
			// On parcourt toutes les clés (KIDs) de l'objet translations
			for (const kid of translationKeys()) {
				const entry = translations[kid];

				// Utilisation de lodash pour retirer les items liés au scope supprimé
				_.remove(entry.items, item => item.scope && item.scope.$id === scopeId);

				// Optionnel : si le KID n'a plus d'items et pas de texte en cache, on peut nettoyer l'entrée
				if (entry.items.length === 0 && Object.keys(entry.values).length === 0) {
					delete translations[kid];
				}
			}
		},

		/**
		 * @param {import('~/../typings/ktranslate').KTranslate.TranslationItem['scope']} scope
		 * @param {import('@knitiv/api-client-javascript').KNode} kid
		 * @param {string} text
		 * @returns {Promise<string>}
		 */
		set: async (scope, kid, text) => {
			const currentLang = $rootScope.appConfig.lang.code;

			if (!translations[kid]) {
				translations[kid] = {
					items: [],
					values:  {},
				};
			}

			// On met à jour le cache
			translations[kid].values[currentLang] = text;

			// Si des requêtes étaient en attente pour ce KID, on les résout immédiatement
			if (translations[kid].items.length > 0) {
				for (const item of translations[kid].items) {
					if (item.scope) {
						item.scope.text = text;
					}
					item.resolve(text);
				}
				translations[kid].items = [];
			}

			if (scope) {
				scope.text = text;
			}
			return text;
		},
	};
};

angular.module('app')
	.factory('KTranslate', KTranslate);
